import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSidebar } from '../../components/Sidebar';
import Sidebar from '../../components/Sidebar';
import HeaderGlobal from '../../components/HeaderGlobal';
import StudentDetail from './StudentDetail';
import { useAuth } from '../../api/AuthContext';
import {
  getCampusDropdown,
  getCiclosEscolaresDropdown,
  getCicloActual,
  getEstudiantesReinscripcion,
  enviarRecordatorioReinscripcion,
} from '../../api/inscripcionReinscripcion';
import styles from '../../styles/InscripcionReinscripcionStyles/InscripcionReinscripcion.module.css';
import LoaderOverlay from '../../components/LoaderOverlay';

const ITEMS_PER_PAGE = 15;

function InscripcionReinscripcionScreen() {
  const { isCollapsed } = useSidebar();
  const { user } = useAuth();

  // --- Dropdowns ---
  const [campusOptions, setCampusOptions] = useState([]);
  const [ciclosOptions, setCiclosOptions] = useState([]);

  // --- Validación de permisos de campus ---
  const userCampusIds = useMemo(() => {
    return (user?.campus_asignados || []).map(id => String(id));
  }, [user]);

  const puedeChangiarCampus = useMemo(() => {
    // Si tiene 2 o más campus asignados, puede cambiar
    // Si es admin/super admin, puede cambiar
    return userCampusIds.length >= 2 || user?.isSysUser || false;
  }, [userCampusIds, user]);

  const campusFiltered = useMemo(() => {
    // Si solo tiene 1 campus, filtrar opciones a solo ese campus
    if (userCampusIds.length === 1) {
      return campusOptions.filter(c => userCampusIds.includes(String(c.id)));
    }
    // Si tiene múltiples campus, mostrar todos
    return campusOptions;
  }, [campusOptions, userCampusIds]);

  // --- Filtros ---
  const [selectedCampus, setSelectedCampus] = useState('');
  const [selectedCiclo, setSelectedCiclo] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');

  // Nombre del campus seleccionado, para mantener sincronizado el selector del HeaderGlobal
  const selectedCampusName = useMemo(() => {
    const found = campusOptions.find((c) => String(c.id) === String(selectedCampus));
    return found ? found.nombre : '';
  }, [campusOptions, selectedCampus]);

  // --- Datos ---
  const [conAdeudo, setConAdeudo] = useState([]);
  const [sinAdeudo, setSinAdeudo] = useState([]);
  const [yaInscritos, setYaInscritos] = useState([]);
  const [cicloSiguiente, setCicloSiguiente] = useState('');
  const [cicloSiguienteId, setCicloSiguienteId] = useState(null);
  const [conceptosPorSeccion, setConceptosPorSeccion] = useState([]);
  const [seccionesSinConcepto, setSeccionesSinConcepto] = useState([]);
  const [aplicaDescuentoPronto, setAplicaDescuentoPronto] = useState(false);
  const [descuentoProntoMonto, setDescuentoProntoMonto] = useState(0);
  const [estudiantesIncompatibles, setEstudiantesIncompatibles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingData, setLoadingData] = useState(false);

  // --- Detalle de estudiante (flujo caja) ---
  const [selectedStudent, setSelectedStudent] = useState(null);

  // --- Paginación y búsqueda por tabla ---
  const [pageConAdeudo, setPageConAdeudo] = useState(1);
  const [pageSinAdeudo, setPageSinAdeudo] = useState(1);
  const [searchConAdeudo, setSearchConAdeudo] = useState('');
  const [searchSinAdeudo, setSearchSinAdeudo] = useState('');
  const [searchYaInscritos, setSearchYaInscritos] = useState('');
  const [pageYaInscritos, setPageYaInscritos] = useState(1);

  // --- Modal de correo ---
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState(''); // 'recordatorio' | 'boleta'
  const [modalAsunto, setModalAsunto] = useState('');
  const [modalMensaje, setModalMensaje] = useState('');
  const [modalDestinatarios, setModalDestinatarios] = useState([]);
  const [enviando, setEnviando] = useState(false);
  const [adjuntoPdf, setAdjuntoPdf] = useState(null);

  // --- Cargar dropdowns ---
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const [campusData, ciclosData, cicloActualData] = await Promise.all([
          getCampusDropdown(),
          getCiclosEscolaresDropdown(),
          getCicloActual().catch(() => null),
        ]);
        setCampusOptions(campusData || []);
        setCiclosOptions(ciclosData || []);
        
        // Auto-detectar ciclo actual por fecha del servidor
        if (cicloActualData?.ciclo_actual?.id_ciclo_escolar) {
          setSelectedCiclo(cicloActualData.ciclo_actual.id_ciclo_escolar);
        } else if (ciclosData?.length > 0) {
          // Fallback: detectar por fechas del dropdown con validación de año
          const hoy = new Date();
          const hoyStr = hoy.toISOString().split('T')[0];
          const anioActual = hoy.getFullYear();
          
          // Filtrar ciclos con año coherente (±2 del año actual)
          const ciclosValidos = ciclosData.filter(
            (c) => c.fecha_inicio && c.fecha_fin && c.anio && Math.abs(c.anio - anioActual) <= 2
          );
          
          const cicloDetectado = (ciclosValidos.length > 0 ? ciclosValidos : ciclosData).find(
            (c) => c.fecha_inicio && c.fecha_fin && hoyStr >= c.fecha_inicio && hoyStr <= c.fecha_fin
          );
          
          if (cicloDetectado) {
            setSelectedCiclo(cicloDetectado.id_ciclo_escolar);
          } else {
            // Último fallback: ciclo más reciente con año coherente
            const cicloReciente = ciclosValidos.length > 0
              ? ciclosValidos.sort((a, b) => (b.fecha_inicio || '').localeCompare(a.fecha_inicio || ''))[0]
              : ciclosData[0];
            setSelectedCiclo(cicloReciente.id_ciclo_escolar);
          }
        }
      } catch (e) {
        console.error('Error cargando dropdowns:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // --- Auto-seleccionar el campus del usuario (una sola vez, al tener opciones y perfil) ---
  useEffect(() => {
    if (selectedCampus) return;            // ya hay un campus elegido
    if (!campusOptions.length) return;     // esperar a que carguen las opciones
    const principalId = user?.id_campus != null ? String(user.id_campus) : '';
    const asignados = (user?.campus_asignados || []).map((id) => String(id));
    let inicial = null;
    // 1) Campus principal del usuario autenticado
    if (principalId) inicial = campusOptions.find((c) => String(c.id) === principalId);
    // 2) Algún campus asignado presente en las opciones
    if (!inicial && asignados.length > 0) inicial = campusOptions.find((c) => asignados.includes(String(c.id)));
    // 3) Fallback: primer campus disponible
    if (!inicial) inicial = campusOptions[0];
    if (inicial) setSelectedCampus(String(inicial.id));
  }, [campusOptions, user, selectedCampus]);

  // --- Debounce búsqueda ---
  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(searchTerm), 400);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // --- Cargar estudiantes clasificados ---
  const fetchSeqRef = useRef(0);
  const fetchStudents = useCallback(async () => {
    if (!selectedCampus || !selectedCiclo) return;
    const seq = ++fetchSeqRef.current;
    setLoadingData(true);
    try {
      const data = await getEstudiantesReinscripcion(selectedCampus, selectedCiclo, searchDebounced);
      // Ignorar respuestas obsoletas (el usuario ya cambió de campus/ciclo/búsqueda)
      if (seq !== fetchSeqRef.current) return;
      setConAdeudo(data.con_adeudo || []);
      setSinAdeudo(data.sin_adeudo || []);
      setYaInscritos(data.ya_inscritos || []);
      setCicloSiguiente(data.ciclo_siguiente || '');
      setCicloSiguienteId(data.ciclo_siguiente_id || null);
      setConceptosPorSeccion(data.conceptos_por_seccion || []);
      setSeccionesSinConcepto(data.secciones_sin_concepto || []);
      setAplicaDescuentoPronto(data.aplica_descuento_pronto_pago || false);
      setDescuentoProntoMonto(data.descuento_pronto_pago_monto || 0);
      setEstudiantesIncompatibles(data.estudiantes_incompatibles || []);
    } catch (err) {
      if (seq !== fetchSeqRef.current) return;
      console.error('Error al cargar estudiantes:', err);
      setConAdeudo([]);
      setSinAdeudo([]);
      setYaInscritos([]);
      setConceptosPorSeccion([]);
      setSeccionesSinConcepto([]);
      setEstudiantesIncompatibles([]);
    } finally {
      if (seq === fetchSeqRef.current) setLoadingData(false);
    }
  }, [selectedCampus, selectedCiclo, searchDebounced]);

  useEffect(() => {
    if (selectedCampus && selectedCiclo) fetchStudents();
  }, [fetchStudents, selectedCampus, selectedCiclo]);

  // --- Resetear paginación cuando cambian los datos o búsquedas por tabla ---
  useEffect(() => { setPageConAdeudo(1); }, [conAdeudo, searchConAdeudo]);
  useEffect(() => { setPageSinAdeudo(1); }, [sinAdeudo, searchSinAdeudo]);
  useEffect(() => { setPageYaInscritos(1); }, [yaInscritos, searchYaInscritos]);

  // --- Filtrado local por tabla ---
  const filterBySearch = (list, term) => {
    if (!term.trim()) return list;
    const lower = term.toLowerCase().trim();
    return list.filter(
      (est) =>
        (est.nombre_completo || '').toLowerCase().includes(lower) ||
        (est.matricula || '').toLowerCase().includes(lower)
    );
  };

  const filteredConAdeudo = filterBySearch(conAdeudo, searchConAdeudo);
  const filteredSinAdeudo = filterBySearch(sinAdeudo, searchSinAdeudo);
  const filteredYaInscritos = filterBySearch(yaInscritos, searchYaInscritos);

  // --- Paginación helpers ---
  const paginate = (list, page) => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    return list.slice(start, start + ITEMS_PER_PAGE);
  };
  const totalPages = (list) => Math.max(1, Math.ceil(list.length / ITEMS_PER_PAGE));

  const paginatedConAdeudo = paginate(filteredConAdeudo, pageConAdeudo);
  const paginatedSinAdeudo = paginate(filteredSinAdeudo, pageSinAdeudo);
  const paginatedYaInscritos = paginate(filteredYaInscritos, pageYaInscritos);

  // --- Modal handlers ---
  const openRecordatorioModal = () => {
    setModalType('recordatorio');
    setModalAsunto('Recordatorio de Pago - Reinscripción');
    setModalMensaje(
      'Te recordamos que para iniciar el proceso de Reinscripción al siguiente Ciclo Escolar, NO debes tener ningún adeudo pendiente y te invitamos a consultar tu estado de cuenta en el Portal de Estudiantes y cubrir los Adeudos Pendientes.'
    );
    setModalDestinatarios(conAdeudo.map((e) => e.id_estudiante));
    setAdjuntoPdf(null);
    setModalOpen(true);
  };

  const openBoletaModal = () => {
    setModalType('boleta');
    setModalAsunto('Confirmación de Inscripción Exitosa');
    setModalMensaje(
      'Te informamos que tu inscripción ha sido aplicada exitosamente para el siguiente Ciclo Escolar. ¡Bienvenido(a) de vuelta! Si tienes alguna duda, acércate al área de Servicios Escolares.'
    );
    setModalDestinatarios(sinAdeudo.map((e) => e.id_estudiante));
    setAdjuntoPdf(null);
    setModalOpen(true);
  };

  const handlePdfChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.toLowerCase().endsWith('.pdf')) {
        alert('Solo se permiten archivos PDF.');
        e.target.value = '';
        setAdjuntoPdf(null);
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        alert('El archivo PDF no debe superar los 10 MB.');
        e.target.value = '';
        setAdjuntoPdf(null);
        return;
      }
      setAdjuntoPdf(file);
    } else {
      setAdjuntoPdf(null);
    }
  };

  const handleSendEmail = async () => {
    if (!modalMensaje.trim() || modalDestinatarios.length === 0) return;
    setEnviando(true);
    try {
      const result = await enviarRecordatorioReinscripcion(
        modalDestinatarios,
        modalMensaje,
        modalAsunto,
        adjuntoPdf
      );
      alert(`Correos enviados: ${result.enviados} de ${result.total}${result.errores?.length ? `\nErrores: ${result.errores.join(', ')}` : ''}`);
      setModalOpen(false);
      setAdjuntoPdf(null);
    } catch {
      alert('Error al enviar los correos.');
    } finally {
      setEnviando(false);
    }
  };

  // --- Click en estudiante (flujo caja) ---
  const handleStudentClick = (student) => {
    // Verificar si la sección del estudiante tiene concepto de cobro
    const seccionEstudiante = student.seccion || 'Sin sección';
    const sinConcepto = seccionesSinConcepto.includes(seccionEstudiante);
    if (sinConcepto) {
      alert(`No se puede continuar con la inscripción: no existe un concepto de cobro de INSCRIPCIÓN/REGISTRO configurado para la sección "${seccionEstudiante}" en el siguiente ciclo escolar. Debe crear el concepto de cobro en la pantalla de Conceptos de Cobro antes de continuar.`);
      return;
    }
    if (student.saldo_pendiente > 0) {
      alert('Este estudiante tiene adeudos pendientes. No puede continuar con la reinscripción hasta cubrir sus pagos.');
      return;
    }
    // Advertir si hay incompatibilidad de tipo de periodo
    if (!student.ciclo_compatible && student.advertencias_ciclo?.length > 0) {
      const tipoProg = student.tipo_periodo?.nombre || 'Sin tipo';
      const tipoCiclo = student.tipo_periodo_ciclo || 'Sin tipo';
      const continuar = window.confirm(
        `⚠️ ADVERTENCIA: Incompatibilidad de tipo de periodo detectada.\n\n` +
        `• Programa del estudiante: ${tipoProg}\n` +
        `• Ciclo siguiente detectado: ${tipoCiclo}\n\n` +
        `${student.advertencias_ciclo.join('\n')}\n\n` +
        `¿Desea continuar con la inscripción de todos modos?`
      );
      if (!continuar) return;
    }
    setSelectedStudent(student);
  };

  const handleBackToList = () => {
    setSelectedStudent(null);
    fetchStudents();
  };

  // --- Vista detalle de estudiante (flujo caja) ---
  if (selectedStudent) {
    return (
      <div className={`${styles.layout} ${isCollapsed ? styles.collapsed : ''}`}>
        <div className={styles.sidebarColumn}>
          <Sidebar />
        </div>
        <div className={styles.contentColumn}>
          <StudentDetail
            student={selectedStudent}
            onBack={handleBackToList}
            searchFilters={{
              campus: selectedCampus,
              cicloEscolar: selectedCiclo,
              cicloSiguienteId: cicloSiguienteId,
              cicloSiguienteNombre: cicloSiguiente || '',
              tipo: 'Reinscripción',
            }}
            searchTerm={searchTerm}
            onSearch={() => {}}
          />
        </div>
      </div>
    );
  }

  // --- Vista principal ---
  return (
    <div className={`${styles.layout} ${isCollapsed ? styles.collapsed : ''}`}>
      <div className={styles.sidebarColumn}>
        <Sidebar />
      </div>
      <div className={styles.contentColumn}>
        <HeaderGlobal
          title="Reinscripción"
          campus={selectedCampusName}
          onCampusChange={(e) => {
            const campusName = e?.target?.value ?? '';
            if (!campusName || campusName === 'Todos') return;
            const found = campusOptions.find(c => c.nombre === campusName);
            if (found && String(found.id) !== String(selectedCampus)) {
              setSelectedCampus(String(found.id));
            }
          }}
        />
        <div className={styles.contentArea}>
          <div className={styles.contentWrapper}>
            <LoaderOverlay visible={loading || loadingData} label="Cargando datos..." />

            {/* ===== Filtros: Ciclo Escolar y Búsqueda ===== */}
            <div className={styles.searchFiltersContainer}>
              <div className={styles.filtersGrid}>
                <div className={styles.filterGroup}>
                  <label className={styles.filterLabel}>Ciclo Escolar</label>
                  <select
                    className={styles.filterSelect}
                    value={selectedCiclo}
                    onChange={(e) => setSelectedCiclo(e.target.value)}
                    disabled={loading}
                  >
                    <option value="">Seleccione un ciclo</option>
                    {ciclosOptions.map((c) => (
                      <option key={c.id_ciclo_escolar} value={c.id_ciclo_escolar}>{c.ciclo}</option>
                    ))}
                  </select>
                </div>
                <div className={styles.filterGroup}>
                  <label className={styles.filterLabel}>Buscar Estudiante</label>
                  <input
                    className={styles.searchInput}
                    type="text"
                    placeholder="Matrícula o nombre..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* ===== Alerta: Secciones sin concepto de cobro ===== */}
            {seccionesSinConcepto.length > 0 && cicloSiguiente && (
              <div style={{
                backgroundColor: '#fff3cd',
                border: '1px solid #ffc107',
                borderLeft: '4px solid #ff9800',
                borderRadius: '8px',
                padding: '16px 20px',
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
              }}>
                <span style={{ fontSize: '24px', flexShrink: 0 }}>⚠️</span>
                <div>
                  <p style={{ margin: '0 0 6px', fontWeight: 'bold', color: '#856404', fontSize: '15px' }}>
                    Faltan conceptos de cobro de INSCRIPCIÓN/REGISTRO para el ciclo {cicloSiguiente}
                  </p>
                  <p style={{ margin: '0 0 8px', color: '#856404', fontSize: '13px' }}>
                    Las siguientes secciones no tienen un concepto de cobro configurado. No se podrá aplicar la inscripción a estudiantes de estas secciones:
                  </p>
                  <ul style={{ margin: '0', paddingLeft: '20px', color: '#856404', fontSize: '13px' }}>
                    {seccionesSinConcepto.map((sec) => (
                      <li key={sec}><strong>{sec}</strong></li>
                    ))}
                  </ul>
                  <p style={{ margin: '8px 0 0', color: '#856404', fontSize: '12px' }}>
                    Vaya a la pantalla de <strong>Conceptos de Cobro</strong> para crear los conceptos faltantes.
                  </p>
                </div>
              </div>
            )}

            {/* ===== Info: Conceptos de cobro por sección ===== */}
            {conceptosPorSeccion.filter(c => c.existe).length > 0 && cicloSiguiente && (
              <div style={{
                backgroundColor: '#d4edda',
                border: '1px solid #c3e6cb',
                borderLeft: '4px solid #28a745',
                borderRadius: '8px',
                padding: '12px 20px',
                marginBottom: '20px',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <span style={{ fontSize: '20px', flexShrink: 0 }}>✅</span>
                  <p style={{ margin: '0', color: '#155724', fontSize: '14px', fontWeight: 'bold' }}>
                    Conceptos de cobro configurados por sección:
                  </p>
                </div>
                {conceptosPorSeccion.filter(c => c.existe).map((c) => (
                  <p key={c.id_seccion} style={{ margin: '4px 0 4px 28px', color: '#155724', fontSize: '13px' }}>
                    <strong>{c.seccion}:</strong> {c.concepto} — ${c.monto?.toLocaleString('es-MX', { minimumFractionDigits: 2 })} — Ciclo: {c.ciclo}
                  </p>
                ))}
              </div>
            )}

            {/* ===== Info: Descuento pronto pago ===== */}
            {aplicaDescuentoPronto && (
              <div style={{
                backgroundColor: '#cce5ff',
                border: '1px solid #b8daff',
                borderLeft: '4px solid #007bff',
                borderRadius: '8px',
                padding: '12px 20px',
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
              }}>
                <span style={{ fontSize: '20px', flexShrink: 0 }}>💰</span>
                <p style={{ margin: '0', color: '#004085', fontSize: '14px' }}>
                  <strong>Descuento pronto pago activo:</strong> Se aplicará un descuento de <strong>${descuentoProntoMonto.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</strong> MXN a los cargos de inscripción generados antes del <strong>18 de abril</strong>.
                </p>
              </div>
            )}

            {/* ===== Alerta: Estudiantes con incompatibilidad de tipo de periodo ===== */}
            {estudiantesIncompatibles.length > 0 && (
              <div style={{
                backgroundColor: '#f8d7da',
                border: '1px solid #f5c6cb',
                borderLeft: '4px solid #dc3545',
                borderRadius: '8px',
                padding: '16px 20px',
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
              }}>
                <span style={{ fontSize: '24px', flexShrink: 0 }}>🔄</span>
                <div>
                  <p style={{ margin: '0 0 6px', fontWeight: 'bold', color: '#721c24', fontSize: '15px' }}>
                    Incompatibilidad de tipo de periodo detectada ({estudiantesIncompatibles.length} estudiante{estudiantesIncompatibles.length > 1 ? 's' : ''})
                  </p>
                  <p style={{ margin: '0 0 8px', color: '#721c24', fontSize: '13px' }}>
                    Los siguientes estudiantes tienen un programa académico cuyo tipo de periodo no coincide con el ciclo siguiente detectado. El ciclo siguiente se asignó por proximidad de fechas:
                  </p>
                  <ul style={{ margin: '0', paddingLeft: '20px', color: '#721c24', fontSize: '13px', maxHeight: '120px', overflowY: 'auto' }}>
                    {estudiantesIncompatibles.slice(0, 10).map((est) => (
                      <li key={est.id_estudiante}>
                        <strong>{est.nombre}</strong> ({est.matricula}) — Programa: {est.tipo_periodo_programa || 'Sin tipo'} / Ciclo: {est.tipo_periodo_ciclo || 'Sin tipo'}
                      </li>
                    ))}
                    {estudiantesIncompatibles.length > 10 && (
                      <li>... y {estudiantesIncompatibles.length - 10} más</li>
                    )}
                  </ul>
                  <p style={{ margin: '8px 0 0', color: '#721c24', fontSize: '12px' }}>
                    Verifique que existan ciclos escolares del tipo correcto (semestral, tetramestral, etc.) para cada programa.
                  </p>
                </div>
              </div>
            )}

            {/* ===== Tabla: Estudiantes CON ADEUDO ===== */}
            <div className={`${styles.tableContainer} ${styles.sectionGap}`}>
              <div className={styles.tableHeader}>
                <div className={styles.tableHeaderRow}>
                  <div className={styles.tableTitleContainer}>
                    <div className={styles.tableIcon}>
                      <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 15h2v-2h-2v2zm0-4h2V7h-2v6z"/></svg>
                    </div>
                    <span className={styles.tableTitle}>Estudiantes con Adeudo ({filteredConAdeudo.length})</span>
                  </div>
                  {conAdeudo.length > 0 && (
                    <button className={styles.btnRecordatorio} onClick={openRecordatorioModal}>
                      📧 Recordatorio de Pago
                    </button>
                  )}
                </div>
                <div className={styles.tableSearchBar}>
                  <input
                    className={styles.tableSearchInput}
                    type="text"
                    placeholder="Buscar por matrícula o nombre..."
                    value={searchConAdeudo}
                    onChange={(e) => setSearchConAdeudo(e.target.value)}
                  />
                </div>
              </div>
              <div className={styles.tableWrapper}>
                {filteredConAdeudo.length === 0 ? (
                  <p style={{ textAlign: 'center', padding: '20px', color: '#898d8d' }}>
                    No se encontraron estudiantes con adeudo
                  </p>
                ) : (
                  <table className={styles.studentsTable}>
                    <thead>
                      <tr>
                        <th>Nombre</th>
                        <th>Matrícula</th>
                        <th>Sección</th>
                        <th>Tipo Periodo</th>
                        <th>Ciclo Actual</th>
                        <th>Ciclo Siguiente</th>
                        <th>Campus</th>
                        <th>Programa</th>
                        <th>Saldo Pendiente</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedConAdeudo.map((est) => (
                        <tr key={est.id_estudiante}>
                          <td>{est.nombre_completo}</td>
                          <td>{est.matricula}</td>
                          <td>{est.seccion || 'Sin sección'}</td>
                          <td>
                            <span style={{
                              padding: '2px 8px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              backgroundColor: est.tipo_periodo ? '#e8f4fd' : '#f0f0f0',
                              color: est.tipo_periodo ? '#0c5460' : '#6c757d',
                            }}>
                              {est.tipo_periodo?.nombre || 'Sin tipo'}
                            </span>
                          </td>
                          <td>
                            <span style={{
                              padding: '2px 8px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              backgroundColor: est.ciclo_actual_compatible !== false ? '#d4edda' : '#fff3cd',
                              color: est.ciclo_actual_compatible !== false ? '#155724' : '#856404',
                            }}>
                              {est.ciclo_escolar || 'N/A'}
                            </span>
                          </td>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              {est.ciclo_siguiente || cicloSiguiente}
                              {!est.ciclo_compatible && est.advertencias_ciclo?.length > 0 && (
                                <span title={est.advertencias_ciclo.join(' | ')} style={{ cursor: 'help', fontSize: '14px' }}>⚠️</span>
                              )}
                            </div>
                          </td>
                          <td>{est.campus}</td>
                          <td>{est.programa}</td>
                          <td>
                            <span className={styles.saldoBadge}>
                              ${est.saldo_pendiente.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              {filteredConAdeudo.length > ITEMS_PER_PAGE && (
                <div className={styles.paginationContainer}>
                  <button
                    className={styles.paginationBtn}
                    disabled={pageConAdeudo <= 1}
                    onClick={() => setPageConAdeudo((p) => p - 1)}
                  >
                    ‹ Anterior
                  </button>
                  {Array.from({ length: totalPages(filteredConAdeudo) }, (_, i) => i + 1).map((num) => (
                    <button
                      key={num}
                      className={`${styles.paginationBtn} ${pageConAdeudo === num ? styles.paginationBtnActive : ''}`}
                      onClick={() => setPageConAdeudo(num)}
                    >
                      {num}
                    </button>
                  ))}
                  <button
                    className={styles.paginationBtn}
                    disabled={pageConAdeudo >= totalPages(filteredConAdeudo)}
                    onClick={() => setPageConAdeudo((p) => p + 1)}
                  >
                    Siguiente ›
                  </button>
                  <span className={styles.paginationInfo}>
                    Página {pageConAdeudo} de {totalPages(filteredConAdeudo)} ({filteredConAdeudo.length} registros)
                  </span>
                </div>
              )}
            </div>

            {/* ===== Tabla: Estudiantes SIN ADEUDO ===== */}
            <div className={styles.tableContainer}>
              <div className={styles.tableHeader}>
                <div className={styles.tableHeaderRow}>
                  <div className={styles.tableTitleContainer}>
                    <div className={styles.tableIcon} style={{ backgroundColor: '#28a745' }}>
                      <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>
                    </div>
                    <span className={styles.tableTitle}>Estudiantes sin Adeudo ({filteredSinAdeudo.length})</span>
                  </div>
                  {sinAdeudo.length > 0 && (
                    <button className={styles.btnBoleta} onClick={openBoletaModal}>
                      ✅ Confirmación de Inscripción
                    </button>
                  )}
                </div>
                <div className={styles.tableSearchBar}>
                  <input
                    className={styles.tableSearchInput}
                    type="text"
                    placeholder="Buscar por matrícula o nombre..."
                    value={searchSinAdeudo}
                    onChange={(e) => setSearchSinAdeudo(e.target.value)}
                  />
                </div>
              </div>
              <div className={styles.tableWrapper}>
                {filteredSinAdeudo.length === 0 ? (
                  <p style={{ textAlign: 'center', padding: '20px', color: '#898d8d' }}>
                    No se encontraron estudiantes sin adeudo
                  </p>
                ) : (
                  <table className={styles.studentsTable}>
                    <thead>
                      <tr>
                        <th>Nombre</th>
                        <th>Matrícula</th>
                        <th>Sección</th>
                        <th>Tipo Periodo</th>
                        <th>Ciclo Actual</th>
                        <th>Ciclo Siguiente</th>
                        <th>Campus</th>
                        <th>Programa</th>
                        <th>Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedSinAdeudo.map((est) => (
                        <tr key={est.id_estudiante}>
                          <td>
                            <button
                              className={styles.studentNameButton}
                              onClick={() => handleStudentClick(est)}
                            >
                              {est.nombre_completo}
                            </button>
                          </td>
                          <td>{est.matricula}</td>
                          <td>{est.seccion || 'Sin sección'}</td>
                          <td>
                            <span style={{
                              padding: '2px 8px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              backgroundColor: est.ciclo_compatible ? '#d4edda' : (est.tipo_periodo ? '#fff3cd' : '#f0f0f0'),
                              color: est.ciclo_compatible ? '#155724' : (est.tipo_periodo ? '#856404' : '#6c757d'),
                            }}>
                              {est.tipo_periodo?.nombre || 'Sin tipo'}
                              {!est.ciclo_compatible && est.tipo_periodo && ' ⚠️'}
                            </span>
                          </td>
                          <td>
                            <span style={{
                              padding: '2px 8px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              backgroundColor: est.ciclo_actual_compatible !== false ? '#d4edda' : '#fff3cd',
                              color: est.ciclo_actual_compatible !== false ? '#155724' : '#856404',
                            }}>
                              {est.ciclo_escolar || 'N/A'}
                            </span>
                          </td>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              {est.ciclo_siguiente || cicloSiguiente}
                              {!est.ciclo_compatible && est.advertencias_ciclo?.length > 0 && (
                                <span title={est.advertencias_ciclo.join(' | ')} style={{ cursor: 'help', fontSize: '14px' }}>⚠️</span>
                              )}
                            </div>
                          </td>
                          <td>{est.campus}</td>
                          <td>{est.programa}</td>
                          <td>
                            <span className={styles.sinAdeudoBadge}>Sin adeudo</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              {filteredSinAdeudo.length > ITEMS_PER_PAGE && (
                <div className={styles.paginationContainer}>
                  <button
                    className={styles.paginationBtn}
                    disabled={pageSinAdeudo <= 1}
                    onClick={() => setPageSinAdeudo((p) => p - 1)}
                  >
                    ‹ Anterior
                  </button>
                  {Array.from({ length: totalPages(filteredSinAdeudo) }, (_, i) => i + 1).map((num) => (
                    <button
                      key={num}
                      className={`${styles.paginationBtn} ${pageSinAdeudo === num ? styles.paginationBtnActive : ''}`}
                      onClick={() => setPageSinAdeudo(num)}
                    >
                      {num}
                    </button>
                  ))}
                  <button
                    className={styles.paginationBtn}
                    disabled={pageSinAdeudo >= totalPages(filteredSinAdeudo)}
                    onClick={() => setPageSinAdeudo((p) => p + 1)}
                  >
                    Siguiente ›
                  </button>
                  <span className={styles.paginationInfo}>
                    Página {pageSinAdeudo} de {totalPages(filteredSinAdeudo)} ({filteredSinAdeudo.length} registros)
                  </span>
                </div>
              )}
            </div>

            {/* ===== Tabla: Estudiantes YA INSCRITOS ===== */}
            {yaInscritos.length > 0 && (
            <div className={styles.tableContainer}>
              <div className={styles.tableHeader}>
                <div className={styles.tableHeaderRow}>
                  <div className={styles.tableTitleContainer}>
                    <div className={styles.tableIcon} style={{ backgroundColor: '#17a2b8' }}>
                      <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>
                    </div>
                    <span className={styles.tableTitle}>Estudiantes Ya Inscritos en Siguiente Ciclo ({filteredYaInscritos.length})</span>
                  </div>
                </div>
                <div className={styles.tableSearchBar}>
                  <input
                    className={styles.tableSearchInput}
                    type="text"
                    placeholder="Buscar por matrícula o nombre..."
                    value={searchYaInscritos}
                    onChange={(e) => setSearchYaInscritos(e.target.value)}
                  />
                </div>
              </div>
              <div className={styles.tableWrapper}>
                <table className={styles.studentsTable}>
                  <thead>
                    <tr>
                      <th>Nombre</th>
                      <th>Matrícula</th>
                      <th>Sección</th>
                      <th>Tipo Periodo</th>
                      <th>Ciclo Actual</th>
                      <th>Ciclo Siguiente</th>
                      <th>Campus</th>
                      <th>Programa</th>
                      <th>Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedYaInscritos.map((est) => (
                      <tr key={est.id_estudiante}>
                        <td>{est.nombre_completo}</td>
                        <td>{est.matricula}</td>
                        <td>{est.seccion || 'Sin sección'}</td>
                        <td>
                          <span style={{
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            backgroundColor: est.tipo_periodo ? '#e8f4fd' : '#f0f0f0',
                            color: est.tipo_periodo ? '#0c5460' : '#6c757d',
                          }}>
                            {est.tipo_periodo?.nombre || 'Sin tipo'}
                          </span>
                        </td>
                        <td>
                          <span style={{
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            backgroundColor: est.ciclo_actual_compatible !== false ? '#d4edda' : '#fff3cd',
                            color: est.ciclo_actual_compatible !== false ? '#155724' : '#856404',
                          }}>
                            {est.ciclo_escolar || 'N/A'}
                          </span>
                        </td>
                        <td>{est.ciclo_siguiente || cicloSiguiente}</td>
                        <td>{est.campus}</td>
                        <td>{est.programa}</td>
                        <td>
                          <span className={styles.sinAdeudoBadge} style={{ backgroundColor: '#17a2b8' }}>Inscrito</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {filteredYaInscritos.length > ITEMS_PER_PAGE && (
                <div className={styles.paginationContainer}>
                  <button
                    className={styles.paginationBtn}
                    disabled={pageYaInscritos <= 1}
                    onClick={() => setPageYaInscritos((p) => p - 1)}
                  >
                    ‹ Anterior
                  </button>
                  {Array.from({ length: totalPages(filteredYaInscritos) }, (_, i) => i + 1).map((num) => (
                    <button
                      key={num}
                      className={`${styles.paginationBtn} ${pageYaInscritos === num ? styles.paginationBtnActive : ''}`}
                      onClick={() => setPageYaInscritos(num)}
                    >
                      {num}
                    </button>
                  ))}
                  <button
                    className={styles.paginationBtn}
                    disabled={pageYaInscritos >= totalPages(filteredYaInscritos)}
                    onClick={() => setPageYaInscritos((p) => p + 1)}
                  >
                    Siguiente ›
                  </button>
                  <span className={styles.paginationInfo}>
                    Página {pageYaInscritos} de {totalPages(filteredYaInscritos)} ({filteredYaInscritos.length} registros)
                  </span>
                </div>
              )}
            </div>
            )}
          </div>
        </div>
      </div>

      {/* ===== Modal de envío de correo ===== */}
      {modalOpen && (
        <div className={styles.modalOverlay} onClick={() => setModalOpen(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>
                {modalType === 'recordatorio' ? 'Recordatorio de Pago' : 'Boleta de Reinscripción'}
              </h3>
              <button className={styles.modalClose} onClick={() => setModalOpen(false)}>✕</button>
            </div>
            <div className={styles.modalBody}>
              <p className={styles.destinatariosCount}>
                Destinatarios: {modalDestinatarios.length} estudiante(s)
              </p>
              <label className={styles.modalLabel}>Asunto</label>
              <input
                className={styles.modalInput}
                value={modalAsunto}
                onChange={(e) => setModalAsunto(e.target.value)}
              />
              <label className={styles.modalLabel}>Mensaje</label>
              <textarea
                className={styles.modalTextarea}
                value={modalMensaje}
                onChange={(e) => setModalMensaje(e.target.value)}
              />
              <label className={styles.modalLabel}>Adjuntar PDF (opcional)</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                <input
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={handlePdfChange}
                  style={{ flex: 1 }}
                />
                {adjuntoPdf && (
                  <button
                    type="button"
                    onClick={() => { setAdjuntoPdf(null); }}
                    style={{ background: 'none', border: 'none', color: '#dc3545', cursor: 'pointer', fontSize: '14px' }}
                  >
                    ✕ Quitar
                  </button>
                )}
              </div>
              {adjuntoPdf && (
                <p style={{ fontSize: '12px', color: '#28a745', margin: '0 0 8px' }}>
                  📎 {adjuntoPdf.name} ({(adjuntoPdf.size / 1024).toFixed(1)} KB)
                </p>
              )}
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.modalBtnCancel} onClick={() => setModalOpen(false)}>
                Cancelar
              </button>
              <button
                className={styles.modalBtnSend}
                onClick={handleSendEmail}
                disabled={enviando}
              >
                {enviando ? 'Enviando...' : 'Enviar Correo'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default InscripcionReinscripcionScreen;
